/** Duração em HH:MM:SS (ou MM:SS abaixo de 1 hora). */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

/** +55 (65) 99272-0644 a partir de 5565992720644. */
export function formatPhone(digits: string | null | undefined): string {
  if (!digits) return '';
  const d = digits.replace(/\D/g, '');
  const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(d);
  return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : digits;
}

/** Número para discar no PBX: DDD + número (sem o 55), como o operador digitaria. */
export function dialableNumber(e164: string | null | undefined): string {
  const d = (e164 ?? '').replace(/\D/g, '');
  return d.startsWith('55') && (d.length === 12 || d.length === 13) ? d.slice(2) : d;
}

const sameDay =(a: Date, b: Date) => a.toDateString() === b.toDateString();

/** Horário curto para listas: hoje = 14:32; antes = 30/09. */
export function shortTime(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  return sameDay(d, now)
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
export const dateOf = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
export const dateTimeOf = (iso: string) => `${dateOf(iso)} ${timeOf(iso)}`;

/** Há quanto tempo (para a fila): "agora", "5 min", "2 h", "3 d". */
export function since(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const min = Math.floor((now - Date.parse(iso)) / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h} h` : `${Math.floor(h / 24)} d`;
}

/** Quem está na ligação: contato, telefone formatado ou ramal. */
export const callPartyLabel = (c: { direction: string; contact: { name: string | null } | null; phone: string | null; fromNumber: string | null; toNumber: string | null }) =>
  c.contact?.name?.trim() || (c.phone ? formatPhone(c.phone) : c.direction === 'outbound' ? (c.toNumber ?? 'número externo') : `Ramal ${c.fromNumber ?? '?'}`);

export const contactLabel = (a: { contactName: string | null; contactPhone: string | null }) =>
  a.contactName?.trim() || formatPhone(a.contactPhone) || 'Contato sem nome';

export const STATUS_LABEL: Record<string, string> = {
  novo: 'Escolhendo equipe',
  na_fila: 'Na fila',
  em_atendimento: 'Em atendimento',
  transferido: 'Transferido',
  finalizado: 'Finalizado',
};

/** Situação para exibir: finalizado distingue cancelado e spam. */
export const statusLabel = (a: { status: string; finishReason?: string | null }) =>
  a.status === 'finalizado' && a.finishReason === 'cancelled' ? 'Cancelado' : a.status === 'finalizado' && a.finishReason === 'spam' ? 'Spam' : (STATUS_LABEL[a.status] ?? a.status);

export const CLOSE_REASON_LABEL: Record<string, string> = {
  switched: 'trocou de atendimento',
  closed_by_operator: 'fechado pelo operador',
  heartbeat_timeout: 'tela sem sinal',
  idle_timeout: 'ociosidade',
  transferred: 'transferido',
  finished: 'finalizado',
  logout: 'saiu do sistema',
  admin_forced: 'encerrado pelo administrador',
  left: 'saiu da ajuda',
  call_ended: 'ligação encerrada',
};

export const SYNC_TYPE_LABEL: Record<string, string> = {
  create_ticket: 'Criar chamado',
  record_attendance: 'Registrar no chamado',
  close_ticket: 'Encerrar chamado',
};

export const SYNC_STATUS_LABEL: Record<string, string> = {
  pending: 'aguardando',
  running: 'em andamento',
  waiting: 'precisa de ação',
  done: 'concluído',
  failed: 'falhou',
};
